export   const devices = [
    // ETHOS (20 devices)
    {
      id: 1,
      name: 'Testimonium',
      category: 'ethos',
      definition: 'Citing credible witnesses or authorities to support your argument',
      example: 'Nelson Mandela often invoked the testimony of imprisoned anti-apartheid activists to authenticate his message of reconciliation',
      instruction: 'Use testimonials from recognised experts or witnesses. Ensure sources are relevant and credible to your audience.',
      usage: 'Political speeches, academic writing, legal arguments',
      origin: 'Latin: testimonium (evidence, witness)'
    },
    {
      id: 2,
      name: 'Auctoritas',
      category: 'ethos',
      definition: 'Establishing authority through experience, expertise or institutional position',
      example: 'Malala Yousafzai speaking on education draws authority from her lived experience as a student activist',
      instruction: 'Establish credentials early. Balance authority with humility to avoid appearing arrogant.',
      usage: 'Professional presentations, expert testimony, leadership communication',
      origin: 'Latin: auctoritas (authority, prestige)'
    },
    {
      id: 3,
      name: 'Ethical Appeal Through Humility',
      category: 'ethos',
      definition: 'Building credibility by acknowledging limitations and showing intellectual honesty',
      example: 'Pope Francis frequently acknowledges the Church\'s historical failures before advocating for reform',
      instruction: 'Admit what you don\'t know. Show willingness to learn. This paradoxically increases trust.',
      usage: 'Crisis communication, reconciliation speeches, scientific discourse',
      origin: 'Classical rhetoric: ethos through modesty'
    },
    {
      id: 4,
      name: 'Association by Virtue',
      category: 'ethos',
      definition: 'Linking yourself to respected figures, institutions or values',
      example: 'African leaders invoking Pan-Africanism connect themselves to Kwame Nkrumah and Julius Nyerere\'s legacy',
      instruction: 'Choose associations carefully. Ensure they resonate with your specific audience.',
      usage: 'Political campaigns, brand positioning, social movements',
      origin: 'Classical ethos: building character through association'
    },
    {
      id: 5,
      name: 'The Lived Experience',
      category: 'ethos',
      definition: 'Drawing authority from personal experience rather than formal credentials',
      example: 'Indigenous environmental activists cite ancestral land stewardship spanning centuries',
      instruction: 'Share personal stories authentically. Connect experience to broader principles.',
      usage: 'Activist speeches, testimonies, community organising',
      origin: 'Modern rhetoric: experiential authority'
    },

    // PATHOS (20 devices)
    {
      id: 6,
      name: 'Narratio Dolor',
      category: 'pathos',
      definition: 'Using stories of suffering to create emotional connection and motivate action',
      example: 'Syrian refugee testimonies at UN forums humanise abstract migration statistics',
      instruction: 'Tell specific stories, not statistics. Show dignity in suffering. Avoid exploitation.',
      usage: 'Humanitarian appeals, social justice advocacy, fundraising',
      origin: 'Latin: narratio (narrative) + dolor (pain, suffering)'
    },
    {
      id: 7,
      name: 'Invocatio Patriae',
      category: 'pathos',
      definition: 'Appealing to love of homeland, cultural identity or collective belonging',
      example: 'Mahatma Gandhi\'s invocation of "Bharat Mata" (Mother India) unified diverse communities',
      instruction: 'Be inclusive. Avoid jingoism. Connect patriotism to universal values.',
      usage: 'National speeches, independence movements, cultural preservation',
      origin: 'Latin: invocatio (invocation) + patria (homeland)'
    },
    {
      id: 8,
      name: 'The Sacred Invocation',
      category: 'pathos',
      definition: 'Appealing to religious or spiritual values shared by the audience',
      example: 'Desmond Tutu\'s "Rainbow Nation" theology united South Africans across faiths',
      instruction: 'Respect religious diversity. Use inclusive spiritual language when possible.',
      usage: 'Interfaith dialogue, moral arguments, peace negotiations',
      origin: 'Religious rhetoric across all traditions'
    },
    {
      id: 9,
      name: 'Futurity Appeal',
      category: 'pathos',
      definition: 'Evoking emotion by invoking future generations and their judgement',
      example: 'Greta Thunberg\'s "How dare you steal my childhood" appeals to intergenerational responsibility',
      instruction: 'Paint vivid pictures of future consequences. Make distant futures feel immediate.',
      usage: 'Climate advocacy, education policy, sustainability campaigns',
      origin: 'Modern environmental rhetoric'
    },
    {
      id: 10,
      name: 'Collective Memory',
      category: 'pathos',
      definition: 'Invoking shared historical experiences to unite or mobilise audience',
      example: 'References to colonialism in African Union speeches create solidarity across nations',
      instruction: 'Acknowledge different perspectives on history. Use shared memory to build, not divide.',
      usage: 'Commemoration speeches, reconciliation processes, national identity',
      origin: 'Collective memory studies + rhetoric'
    },

    // LOGOS (20 devices)
    {
      id: 11,
      name: 'Syllogismus',
      category: 'logos',
      definition: 'Formal logical argument: if premises are true, conclusion must follow',
      example: 'All humans need clean water. Rural villagers are human. Therefore, rural villagers need clean water infrastructure.',
      instruction: 'State premises clearly. Ensure logical connection. Test for fallacies.',
      usage: 'Academic writing, legal briefs, policy proposals',
      origin: 'Greek: syllogismos (deductive reasoning)'
    },
    {
      id: 12,
      name: 'Statistical Proof',
      category: 'logos',
      definition: 'Using quantitative data and research findings to support claims',
      example: 'WHO reports showing vaccine efficacy rates across diverse global populations',
      instruction: 'Cite credible sources. Present data visually when possible. Explain significance.',
      usage: 'Scientific papers, policy advocacy, journalism',
      origin: 'Modern evidence-based rhetoric'
    },
    {
      id: 13,
      name: 'Causa et Effectum',
      category: 'logos',
      definition: 'Demonstrating causal relationships between events or actions',
      example: 'Economists showing how microfinance programs reduce poverty in Bangladesh',
      instruction: 'Distinguish correlation from causation. Acknowledge complexity. Show mechanism.',
      usage: 'Policy analysis, scientific writing, investigative journalism',
      origin: 'Latin: causa (cause) + effectum (effect)'
    },
    {
      id: 14,
      name: 'Definitio',
      category: 'logos',
      definition: 'Establishing clear definitions to frame the debate',
      example: 'Defining "food security" broadly to include nutrition, not just caloric intake',
      instruction: 'Define key terms early. Acknowledge competing definitions. Build from common ground.',
      usage: 'Academic debates, legal arguments, policy framing',
      origin: 'Latin: definitio (boundary, definition)'
    },
    {
      id: 15,
      name: 'Analogia',
      category: 'logos',
      definition: 'Drawing logical parallels between similar situations or structures',
      example: 'Comparing climate change to historical pandemics: both require global coordination',
      instruction: 'Ensure similarity is genuine. Acknowledge differences. Use familiar analogies.',
      usage: 'Education, persuasive writing, complex explanations',
      origin: 'Greek: analogia (proportion, comparison)'
    },

    // KAIROS (20 devices)
    {
      id: 16,
      name: 'Carpe Momentum',
      category: 'kairos',
      definition: 'Recognising and acting upon a fleeting window of opportunity in discourse',
      example: 'Nelson Mandela\'s 1994 inaugural speech timing after 27 years imprisonment created perfect moment for reconciliation message',
      instruction: 'Read the room. Sense when audience is receptive. Strike when emotions peak.',
      usage: 'Political speeches, negotiations, crisis response',
      origin: 'Latin: carpe (seize) + momentum (movement, moment)'
    },
    {
      id: 17,
      name: 'The Silence Eloquent',
      category: 'kairos',
      definition: 'Using calculated silence as powerful rhetoric',
      example: 'Martin Luther King Jr.\'s pauses in "I Have a Dream" let weight of words settle',
      instruction: 'Pause after important statements. Let silence create anticipation. Trust the void.',
      usage: 'Public speaking, dramatic performance, negotiations',
      origin: 'Japanese ma (間): meaningful interval'
    },
    {
      id: 18,
      name: 'Memento Tempus',
      category: 'kairos',
      definition: 'Timing your argument by connecting to collective historical consciousness',
      example: 'Invoking partition of India/Pakistan during contemporary peace talks between nations',
      instruction: 'Know your audience\'s history. Connect past to present. Make history feel alive.',
      usage: 'Diplomatic speeches, commemoration, reconciliation',
      origin: 'Latin: memento (remember) + tempus (time)'
    },
    {
      id: 19,
      name: 'Watershed Invocation',
      category: 'kairos',
      definition: 'Proclaiming a moment as historically pivotal to create momentum',
      example: 'Declaring "This is our moon shot moment" in climate discourse creates urgency',
      instruction: 'Recognise genuine turning points. Create sense of historical significance. Motivate action.',
      usage: 'Political campaigns, social movements, corporate vision',
      origin: 'Modern rhetoric: manufactured kairos'
    },
    {
      id: 20,
      name: 'The Posthumous Vindication',
      category: 'kairos',
      definition: 'Appeals to how history will judge current actions',
      example: '"Our children will ask what we did during the climate crisis" frames present as future judgement',
      instruction: 'Invoke future generations. Create accountability across time. Make legacy tangible.',
      usage: 'Environmental advocacy, ethics debates, legacy planning',
      origin: 'Intergenerational ethics + rhetoric'
    },

    // FIGURES OF SPEECH (50 devices)
    {
      id: 21,
      name: 'Metaphora',
      category: 'figures',
      definition: 'Comparing two unlike things by stating one IS the other',
      example: 'Desmond Tutu\'s "Rainbow Nation" - South Africa as spectrum of unified diversity',
      instruction: 'Choose fresh metaphors. Avoid clichés. Ensure cultural resonance.',
      usage: 'Poetry, speeches, branding, teaching',
      origin: 'Greek: metaphora (transfer, carry over)'
    },
    {
      id: 22,
      name: 'Anaphora',
      category: 'figures',
      definition: 'Repetition of words at the beginning of successive clauses',
      example: 'Martin Luther King: "I have a dream... I have a dream... I have a dream..."',
      instruction: 'Use for emphasis. Build momentum. Limit to 3-5 repetitions for impact.',
      usage: 'Speeches, poetry, calls to action',
      origin: 'Greek: anaphora (carrying back)'
    },
    {
      id: 23,
      name: 'Chiasmus',
      category: 'figures',
      definition: 'Reversing structure of phrases for rhetorical effect (AB-BA pattern)',
      example: 'John F. Kennedy: "Ask not what your country can do for you—ask what you can do for your country"',
      instruction: 'Create mirror structure. Use for memorable conclusions. Balance elements carefully.',
      usage: 'Memorable quotes, speeches, conclusions',
      origin: 'Greek: chiasmos (cross-wise arrangement)'
    },
    {
      id: 24,
      name: 'Hyperbole',
      category: 'figures',
      definition: 'Exaggeration for emphasis or effect',
      example: 'Wangari Maathai: "We cannot tire or give up. We owe it to the present and future generations"—suggesting eternal duty',
      instruction: 'Use sparingly. Ensure audience recognises exaggeration. Avoid in scientific contexts.',
      usage: 'Persuasive writing, activism, emphasis',
      origin: 'Greek: hyperbole (excess, throwing beyond)'
    },
    {
      id: 25,
      name: 'Alliteration',
      category: 'figures',
      definition: 'Repetition of initial consonant sounds in neighbouring words',
      example: 'Nelson Mandela: "Never, never and never again shall it be that this beautiful land..."',
      instruction: 'Use for rhythm and memorability. Don\'t force it. Serves emphasis.',
      usage: 'Speeches, poetry, slogans, branding',
      origin: 'Latin: ad (to) + littera (letter)'
    },

    // ETHOS (15 more devices - total 25)
    {
      id: 26,
      name: 'Professional Credentials',
      category: 'ethos',
      definition: 'Establishing credibility through formal qualifications and certifications',
      example: 'Dr. Wangari Maathai citing her doctoral research in veterinary anatomy when advocating for environmental conservation',
      instruction: 'Present credentials naturally. Don\'t overemphasise. Connect expertise to topic.',
      usage: 'Academic conferences, expert testimony, professional consultations',
      origin: 'Modern professional rhetoric'
    },
    {
      id: 27,
      name: 'Moral Character Display',
      category: 'ethos',
      definition: 'Demonstrating ethical principles through consistent action over time',
      example: 'Mahatma Gandhi\'s decades of simple living authenticated his message of non-materialism',
      instruction: 'Show, don\'t tell. Let actions speak. Acknowledge personal struggles.',
      usage: 'Leadership communication, moral arguments, spiritual teachings',
      origin: 'Aristotelian ethos: character through conduct'
    },
    {
      id: 28,
      name: 'Witness Testimony',
      category: 'ethos',
      definition: 'Using firsthand accounts to establish truth and authenticity',
      example: 'Truth and Reconciliation Commission testimonies in South Africa carried weight through direct experience',
      instruction: 'Protect witness dignity. Provide context. Allow emotional truth.',
      usage: 'Legal proceedings, historical documentation, journalism',
      origin: 'Legal and journalistic traditions'
    },
    {
      id: 29,
      name: 'Institutional Authority',
      category: 'ethos',
      definition: 'Drawing credibility from organisational position or affiliation',
      example: 'WHO Director-General speaking on pandemic response draws on institutional legitimacy',
      instruction: 'Balance institutional power with personal humility. Acknowledge limitations.',
      usage: 'Official statements, policy announcements, institutional communications',
      origin: 'Bureaucratic and organisational rhetoric'
    },
    {
      id: 30,
      name: 'Cultural Elder Status',
      category: 'ethos',
      definition: 'Authority derived from age, wisdom and cultural knowledge',
      example: 'Aboriginal elders speaking on land rights carry authority through generational knowledge',
      instruction: 'Respect cultural protocols. Honour traditions. Bridge old and new wisdom.',
      usage: 'Indigenous rights, cultural preservation, intergenerational dialogue',
      origin: 'Oral tradition and elder wisdom systems'
    },
    {
      id: 31,
      name: 'Peer Recognition',
      category: 'ethos',
      definition: 'Credibility established through awards, honours and professional acclaim',
      example: 'Nobel laureates Malala Yousafzai and Kailash Satyarthi speaking on child rights',
      instruction: 'Mention recognition humbly. Focus on mission, not accolades.',
      usage: 'Award acceptance speeches, expert panels, professional bios',
      origin: 'Academic and professional recognition systems'
    },
    {
      id: 32,
      name: 'Survivor Credibility',
      category: 'ethos',
      definition: 'Authority earned through enduring and overcoming hardship',
      example: 'Holocaust survivors speaking on genocide prevention carry unimpeachable authority',
      instruction: 'Honour resilience. Avoid exploitation. Connect personal to universal.',
      usage: 'Human rights advocacy, trauma education, resilience narratives',
      origin: 'Testimony and witness traditions'
    },
    {
      id: 33,
      name: 'Scholarly Citation',
      category: 'ethos',
      definition: 'Building credibility by referencing respected academic research',
      example: 'Climate scientists citing peer-reviewed studies in IPCC reports',
      instruction: 'Choose credible sources. Explain significance. Make research accessible.',
      usage: 'Academic writing, policy briefs, science communication',
      origin: 'Academic citation practices'
    },
    {
      id: 34,
      name: 'Community Standing',
      category: 'ethos',
      definition: 'Authority derived from long-term community engagement and trust',
      example: 'Local healers in rural India carry credibility through decades of service',
      instruction: 'Build trust over time. Serve consistently. Listen before speaking.',
      usage: 'Community organising, grassroots leadership, local politics',
      origin: 'Community-based authority systems'
    },
    {
      id: 35,
      name: 'Philosophical Lineage',
      category: 'ethos',
      definition: 'Establishing authority by connecting to philosophical or intellectual traditions',
      example: 'Buddhist teachers citing lineage back to historical masters',
      instruction: 'Honour tradition while adapting. Show continuity and innovation.',
      usage: 'Spiritual teaching, philosophical discourse, intellectual history',
      origin: 'Eastern and Western philosophical traditions'
    },
    {
      id: 36,
      name: 'Artistic Achievement',
      category: 'ethos',
      definition: 'Credibility established through recognised creative excellence',
      example: 'Chimamanda Ngozi Adichie speaking on storytelling and African identity',
      instruction: 'Connect art to message. Show how craft informs perspective.',
      usage: 'Cultural commentary, arts advocacy, creative leadership',
      origin: 'Artistic and literary authority'
    },
    {
      id: 37,
      name: 'Technical Expertise',
      category: 'ethos',
      definition: 'Authority from specialised technical knowledge and skill',
      example: 'Engineers from diverse nations collaborating on International Space Station',
      instruction: 'Translate technical knowledge for audiences. Show practical application.',
      usage: 'Technical presentations, engineering proposals, scientific explanations',
      origin: 'Professional and technical expertise'
    },
    {
      id: 38,
      name: 'Religious Authority',
      category: 'ethos',
      definition: 'Credibility derived from spiritual office or religious scholarship',
      example: 'Dalai Lama speaking on compassion draws on Buddhist spiritual authority',
      instruction: 'Respect diverse beliefs. Find universal ethical ground. Avoid dogmatism.',
      usage: 'Interfaith dialogue, moral teaching, spiritual guidance',
      origin: 'Religious and spiritual traditions worldwide'
    },
    {
      id: 39,
      name: 'Entrepreneurial Success',
      category: 'ethos',
      definition: 'Authority from building successful enterprises or innovations',
      example: 'Muhammad Yunus speaking on microfinance draws on Grameen Bank\'s success',
      instruction: 'Share lessons learned. Acknowledge failures. Focus on impact.',
      usage: 'Business presentations, innovation talks, entrepreneurship education',
      origin: 'Business and innovation rhetoric'
    },
    {
      id: 40,
      name: 'The Reluctant Leader',
      category: 'ethos',
      definition: 'Credibility gained by accepting responsibility despite personal reluctance',
      example: 'Aung San Suu Kyi\'s initial reluctance before accepting leadership in Myanmar democracy movement',
      instruction: 'Show authentic reluctance. Emphasise duty over ambition. Build through service.',
      usage: 'Political transitions, crisis leadership, reluctant authority',
      origin: 'Classical leadership narratives'
    },
    {
      id: 131,
      name: 'The Reformed Character',
      category: 'ethos',
      definition: 'Building credibility through personal transformation and lessons learned from past mistakes',
      example: 'Former gang members advocating against youth violence draw authority from their redemption journey',
      instruction: 'Show genuine change. Connect past mistakes to present wisdom. Demonstrate sustained commitment.',
      usage: 'Rehabilitation advocacy, anti-violence campaigns, personal development',
      origin: 'Redemption narratives in classical and religious rhetoric'
    },
    {
      id: 132,
      name: 'Professional Consensus',
      category: 'ethos',
      definition: 'Establishing credibility by citing widespread agreement among experts in a field',
      example: '97% of climate scientists agree on human-caused climate change - IPCC reports',
      instruction: 'Reference established professional bodies. Show breadth of expert agreement. Use specific percentages.',
      usage: 'Scientific communication, policy advocacy, medical guidance',
      origin: 'Modern scientific discourse'
    },
    {
      id: 133,
      name: 'The Bridge Builder',
      category: 'ethos',
      definition: 'Gaining trust by demonstrating connections across different communities or perspectives',
      example: 'Barack Obama\'s positioning as someone who bridges racial and cultural divides',
      instruction: 'Highlight genuine connections to multiple groups. Show understanding of diverse viewpoints.',
      usage: 'Diplomatic communication, community organising, reconciliation efforts',
      origin: 'Modern political rhetoric'
    },
    {
      id: 134,
      name: 'Institutional Legacy',
      category: 'ethos',
      definition: 'Drawing authority from a long-standing institution or tradition',
      example: 'The BBC citing its century-long history of independent journalism',
      instruction: 'Reference institutional history. Connect past achievements to current credibility.',
      usage: 'Brand communication, institutional messaging, heritage marketing',
      origin: 'Institutional rhetoric'
    },
    {
      id: 135,
      name: 'The Whistleblower\'s Courage',
      category: 'ethos',
      definition: 'Credibility gained through personal sacrifice for truth or justice',
      example: 'Edward Snowden\'s credibility on surveillance stemming from his sacrifices to expose it',
      instruction: 'Emphasise personal cost. Show commitment to principle over comfort. Maintain consistency.',
      usage: 'Investigative journalism, activism, accountability movements',
      origin: 'Modern accountability rhetoric'
    },

    // PATHOS (15 more devices - total 25)
    {
      id: 41,
      name: 'The Innocent Victim',
      category: 'pathos',
      definition: 'Evoking sympathy through stories of blameless suffering',
      example: 'UNICEF campaigns showing children affected by war through no fault of their own',
      instruction: 'Preserve dignity. Avoid exploiting suffering. Show agency where possible.',
      usage: 'Humanitarian appeals, child welfare advocacy, disaster relief',
      origin: 'Humanitarian and charitable rhetoric'
    },
    {
      id: 42,
      name: 'Hope Against Odds',
      category: 'pathos',
      definition: 'Inspiring emotion through stories of perseverance despite overwhelming challenges',
      example: 'Refugees rebuilding lives in new countries inspire through resilience',
      instruction: 'Balance realism with hope. Show concrete progress. Avoid false optimism.',
      usage: 'Motivational speeches, recovery narratives, resilience stories',
      origin: 'Inspirational and motivational rhetoric'
    },
    {
      id: 43,
      name: 'Ancestral Honour',
      category: 'pathos',
      definition: 'Appealing to duty and emotion through honouring ancestors',
      example: 'Maori leaders invoking tupuna (ancestors) in treaty negotiations',
      instruction: 'Respect cultural specificity. Connect past to present. Show continuity.',
      usage: 'Indigenous rights, cultural preservation, genealogical appeals',
      origin: 'Ancestral and kinship-based cultures'
    },
    {
      id: 44,
      name: 'The Beautiful Sacrifice',
      category: 'pathos',
      definition: 'Moving audiences through noble self-sacrifice for greater good',
      example: 'Healthcare workers during pandemics sacrificing safety for patient care',
      instruction: 'Honour sacrifice genuinely. Avoid romanticising loss. Provide support.',
      usage: 'Memorial speeches, crisis recognition, valor acknowledgement',
      origin: 'Heroic and sacrificial narratives'
    },
    {
      id: 45,
      name: 'The Shared Struggle',
      category: 'pathos',
      definition: 'Creating solidarity through common challenges and experiences',
      example: 'Pan-African unity movements emphasising shared colonial histories',
      instruction: 'Find genuine common ground. Respect differences. Build collective identity.',
      usage: 'Social movements, labour organising, collective action',
      origin: 'Labour and social movement rhetoric'
    },
    {
      id: 46,
      name: 'Fear of Loss',
      category: 'pathos',
      definition: 'Motivating action through highlighting what stands to be lost',
      example: 'Indigenous language advocates showing cultural extinction if languages die',
      instruction: 'Use ethically. Provide solutions. Avoid manipulation through terror.',
      usage: 'Conservation campaigns, prevention messaging, risk communication',
      origin: 'Fear appeal traditions'
    },
    {
      id: 47,
      name: 'Joy of Achievement',
      category: 'pathos',
      definition: 'Inspiring through celebration of collective accomplishment',
      example: 'South Africa celebrating 1994 democratic elections as collective triumph',
      instruction: 'Share credit widely. Include all contributors. Look forward.',
      usage: 'Victory speeches, milestone celebrations, achievement recognition',
      origin: 'Celebratory and commemorative rhetoric'
    },
    {
      id: 48,
      name: 'The Broken Promise',
      category: 'pathos',
      definition: 'Evoking anger and commitment through unfulfilled commitments',
      example: 'Treaty violations against Indigenous peoples mobilise emotional response',
      instruction: 'Document promises clearly. Show impact of breach. Demand accountability.',
      usage: 'Accountability campaigns, treaty rights, broken social contracts',
      origin: 'Legal and moral accountability rhetoric'
    },
    {
      id: 49,
      name: 'Dignity Restored',
      category: 'pathos',
      definition: 'Moving audiences through narratives of regained human dignity',
      example: 'Anti-apartheid victory speeches celebrating restoration of humanity',
      instruction: 'Centre agency of those who gained dignity. Celebrate without triumphalism.',
      usage: 'Liberation movements, human rights victories, dignity campaigns',
      origin: 'Human rights and dignity-based rhetoric'
    },
    {
      id: 50,
      name: 'The Vulnerable Child',
      category: 'pathos',
      definition: 'Appealing to protective instincts through children at risk',
      example: 'Malnutrition campaigns showing specific children\'s faces and stories',
      instruction: 'Protect privacy. Show solutions. Avoid poverty porn.',
      usage: 'Child welfare, education advocacy, family support campaigns',
      origin: 'Child welfare and protection rhetoric'
    },
    {
      id: 51,
      name: 'Righteous Anger',
      category: 'pathos',
      definition: 'Channeling moral outrage into productive action',
      example: 'Martin Luther King Jr.\'s "fierce urgency of now" mobilised through moral anger',
      instruction: 'Direct anger toward systems, not individuals. Offer constructive channels.',
      usage: 'Social justice, reform movements, moral campaigns',
      origin: 'Prophetic and reform traditions'
    },
    {
      id: 52,
      name: 'Tender Vulnerability',
      category: 'pathos',
      definition: 'Building connection through showing authentic human fragility',
      example: 'Jacinda Ardern\'s emotional response to Christchurch massacre showed leadership vulnerability',
      instruction: 'Be genuinely vulnerable. Balance strength with humanity. Show healing.',
      usage: 'Crisis leadership, trauma response, empathetic communication',
      origin: 'Modern emotional authenticity in leadership'
    },
    {
      id: 53,
      name: 'Pride in Identity',
      category: 'pathos',
      definition: 'Evoking positive emotions through cultural or group identity affirmation',
      example: 'Black Pride movement reclaiming African heritage and beauty standards',
      instruction: 'Affirm without denigrating others. Build positive identity. Celebrate heritage.',
      usage: 'Identity movements, cultural celebration, pride campaigns',
      origin: 'Identity politics and cultural movements'
    },
    {
      id: 54,
      name: 'The Last Chance',
      category: 'pathos',
      definition: 'Creating urgency through finality and irreversibility',
      example: 'Extinction Rebellion highlighting species permanently disappearing',
      instruction: 'Be accurate about timelines. Provide agency. Avoid apocalypticism.',
      usage: 'Environmental advocacy, deadline campaigns, urgent reforms',
      origin: 'Apocalyptic and urgency-based rhetoric'
    },
    {
      id: 55,
      name: 'Gratitude and Debt',
      category: 'pathos',
      definition: 'Appealing through acknowledging gifts received and obligations owed',
      example: 'Veterans\' advocates highlighting society\'s debt to those who served',
      instruction: 'Show genuine appreciation. Translate gratitude to action. Honour commitment.',
      usage: 'Service recognition, reciprocity appeals, moral obligations',
      origin: 'Gift economy and reciprocity traditions'
    },
    {
      id: 136,
      name: 'The Silent Suffering',
      category: 'pathos',
      definition: 'Drawing attention to pain that goes unnoticed or unspoken',
      example: 'Mental health campaigns revealing the hidden struggles behind smiling faces',
      instruction: 'Give voice to the voiceless. Show what is typically hidden. Create safe space for sharing.',
      usage: 'Mental health advocacy, invisible illness campaigns, marginalised voices',
      origin: 'Contemporary awareness movements'
    },
    {
      id: 137,
      name: 'Collective Joy',
      category: 'pathos',
      definition: 'Creating shared emotional uplift and communal celebration',
      example: 'Nelson Mandela\'s inauguration speech bringing together a divided nation in celebration',
      instruction: 'Create inclusive moments. Share credit widely. Build on positive momentum.',
      usage: 'Victory speeches, national celebrations, team achievements',
      origin: 'Celebratory and unifying rhetoric'
    },
    {
      id: 138,
      name: 'The Betrayed Trust',
      category: 'pathos',
      definition: 'Evoking anger and disappointment through violated expectations',
      example: 'Consumer advocates highlighting corporate deception: "They promised safety, they delivered poison"',
      instruction: 'Document the promise. Show the violation. Channel anger constructively.',
      usage: 'Accountability campaigns, whistleblowing, broken promises',
      origin: 'Accountability and trust-based rhetoric'
    },
    {
      id: 139,
      name: 'Protective Instinct',
      category: 'pathos',
      definition: 'Appealing to the desire to defend the vulnerable or precious',
      example: 'David Attenborough\'s plea to protect endangered species for future generations',
      instruction: 'Show what\'s at stake. Make it personal. Provide clear protective actions.',
      usage: 'Environmental protection, child safety, heritage preservation',
      origin: 'Conservation and protection movements'
    },
    {
      id: 140,
      name: 'The Shared Wound',
      category: 'pathos',
      definition: 'Building solidarity through collective trauma or hardship',
      example: 'Post-9/11 speeches emphasising "We all bleed the same" to unite Americans',
      instruction: 'Acknowledge shared pain. Avoid minimising individual experiences. Build collective healing.',
      usage: 'Crisis response, national trauma, community resilience',
      origin: 'Trauma and solidarity rhetoric'
    },

    // LOGOS (15 more devices - total 25)
    {
      id: 56,
      name: 'Inductive Reasoning',
      category: 'logos',
      definition: 'Drawing general conclusions from specific observations',
      example: 'Epidemiologists observing disease patterns across multiple regions to identify transmission vectors',
      instruction: 'Gather sufficient examples. Acknowledge limitations. Test generalisations.',
      usage: 'Scientific research, pattern recognition, theory building',
      origin: 'Scientific method and empirical reasoning'
    },
    {
      id: 57,
      name: 'Deductive Reasoning',
      category: 'logos',
      definition: 'Applying general principles to specific cases',
      example: 'If all humans have rights, and refugees are humans, then refugees have rights',
      instruction: 'Ensure premises are true. Check logical validity. Make implicit premises explicit.',
      usage: 'Legal reasoning, philosophical arguments, policy application',
      origin: 'Aristotelian logic'
    },
    {
      id: 58,
      name: 'Cost-Benefit Analysis',
      category: 'logos',
      definition: 'Weighing advantages against disadvantages systematically',
      example: 'Public health officials weighing lockdown economic costs against lives saved',
      instruction: 'Quantify where possible. Acknowledge intangibles. Show methodology.',
      usage: 'Policy analysis, business decisions, resource allocation',
      origin: 'Economic and utilitarian reasoning'
    },
    {
      id: 59,
      name: 'Historical Precedent',
      category: 'logos',
      definition: 'Using past examples to predict or justify current actions',
      example: 'Citing Marshall Plan success when proposing global economic recovery programs',
      instruction: 'Ensure contexts are comparable. Acknowledge differences. Learn from failures.',
      usage: 'Policy debates, legal arguments, strategic planning',
      origin: 'Historical and comparative reasoning'
    },
    {
      id: 60,
      name: 'Scientific Evidence',
      category: 'logos',
      definition: 'Supporting claims with peer-reviewed research and data',
      example: 'Climate scientists presenting ice core data showing atmospheric CO2 levels over millennia',
      instruction: 'Cite credible sources. Explain methods. Show reproducibility.',
      usage: 'Scientific communication, evidence-based policy, academic discourse',
      origin: 'Scientific method and empiricism'
    },
    {
      id: 61,
      name: 'Mathematical Proof',
      category: 'logos',
      definition: 'Demonstrating truth through mathematical logic and calculation',
      example: 'Economists using mathematical models to show inequality\'s compound effects',
      instruction: 'Show work clearly. Explain assumptions. Make math accessible.',
      usage: 'Economics, engineering, quantitative analysis',
      origin: 'Mathematical and logical traditions'
    },
    {
      id: 62,
      name: 'Expert Consensus',
      category: 'logos',
      definition: 'Appealing to widespread agreement among qualified specialists',
      example: '97% of climate scientists agreeing on anthropogenic climate change',
      instruction: 'Show methodology of consensus. Acknowledge dissent fairly. Explain qualifications.',
      usage: 'Science communication, policy justification, expert testimony',
      origin: 'Scientific consensus building'
    },
    {
      id: 63,
      name: 'Logical Necessity',
      category: 'logos',
      definition: 'Showing that conclusion must follow from premises',
      example: 'If water boils at 100°C at sea level, and this is sea level, water will boil at 100°C',
      instruction: 'Ensure true necessity. Check hidden assumptions. Verify conditions.',
      usage: 'Mathematical proofs, philosophical arguments, scientific laws',
      origin: 'Formal logic and necessity'
    },
    {
      id: 64,
      name: 'Comparative Analysis',
      category: 'logos',
      definition: 'Evaluating by systematically comparing multiple options',
      example: 'Comparing healthcare systems across Nordic countries, UK, and Canada to inform reform',
      instruction: 'Use consistent criteria. Present fairly. Acknowledge trade-offs.',
      usage: 'Policy analysis, product evaluation, system comparisons',
      origin: 'Analytical and comparative methods'
    },
    {
      id: 65,
      name: 'Reductio ad Absurdum',
      category: 'logos',
      definition: 'Disproving by showing a position leads to absurd conclusions',
      example: 'If we say poverty is deserved, then children deserve poverty—an absurd conclusion',
      instruction: 'Follow logic rigorously. Show genuine absurdity. Don\'t create strawmen.',
      usage: 'Philosophical debate, policy critique, logical argumentation',
      origin: 'Greek philosophical tradition'
    },
    {
      id: 66,
      name: 'Empirical Observation',
      category: 'logos',
      definition: 'Basing arguments on direct, verifiable observation',
      example: 'Jane Goodall\'s chimpanzee research based on decades of field observation',
      instruction: 'Document carefully. Allow verification. Distinguish observation from interpretation.',
      usage: 'Scientific research, investigative journalism, field studies',
      origin: 'Empirical and observational sciences'
    },
    {
      id: 67,
      name: 'Systematic Classification',
      category: 'logos',
      definition: 'Organising information into clear categories for analysis',
      example: 'Linnaeus\'s taxonomic system organising life forms into hierarchical categories',
      instruction: 'Use consistent criteria. Allow for edge cases. Explain rationale.',
      usage: 'Scientific organisation, information architecture, analytical frameworks',
      origin: 'Scientific taxonomy and classification'
    },
    {
      id: 68,
      name: 'Probabilistic Reasoning',
      category: 'logos',
      definition: 'Making arguments based on likelihood and statistical probability',
      example: 'Actuaries calculating insurance risk based on statistical models',
      instruction: 'Show confidence intervals. Explain uncertainty. Communicate risk clearly.',
      usage: 'Risk assessment, insurance, public health planning',
      origin: 'Statistics and probability theory'
    },
    {
      id: 69,
      name: 'Falsifiability Test',
      category: 'logos',
      definition: 'Strengthening claims by showing they can be tested and potentially disproven',
      example: 'Karl Popper\'s principle: scientific claims must be falsifiable to be meaningful',
      instruction: 'Make testable predictions. Welcome scrutiny. Define failure conditions.',
      usage: 'Scientific method, critical thinking, hypothesis testing',
      origin: 'Philosophy of science'
    },
    {
      id: 70,
      name: 'Occam\'s Razor',
      category: 'logos',
      definition: 'Preferring simpler explanations when multiple theories fit the evidence',
      example: 'Preferring germ theory over miasma theory for disease transmission',
      instruction: 'Ensure simplicity doesn\'t sacrifice accuracy. Test simple explanations first.',
      usage: 'Scientific theory selection, problem-solving, analytical thinking',
      origin: 'Medieval scholastic philosophy'
    },
    {
      id: 141,
      name: 'Data Triangulation',
      category: 'logos',
      definition: 'Strengthening claims by corroborating evidence from multiple independent sources',
      example: 'Climate scientists using ice cores, tree rings, and ocean sediments to confirm temperature trends',
      instruction: 'Use diverse data sources. Show convergence. Address contradictions.',
      usage: 'Research validation, investigative journalism, forensic analysis',
      origin: 'Research methodology'
    },
    {
      id: 142,
      name: 'Precedent Analysis',
      category: 'logos',
      definition: 'Arguing based on relevant past cases and their outcomes',
      example: 'Legal arguments citing Brown v. Board of Education to support civil rights cases',
      instruction: 'Select relevant precedents. Show parallels clearly. Acknowledge differences.',
      usage: 'Legal reasoning, policy advocacy, historical arguments',
      origin: 'Legal and historical reasoning'
    },
    {
      id: 143,
      name: 'Threshold Arguments',
      category: 'logos',
      definition: 'Identifying critical tipping points where change becomes inevitable or necessary',
      example: '1.5°C warming threshold in climate science marking dangerous climate change',
      instruction: 'Define thresholds clearly. Explain consequences. Show evidence for the boundary.',
      usage: 'Environmental advocacy, public health, risk management',
      origin: 'Systems theory and tipping point analysis'
    },
    {
      id: 144,
      name: 'Root Cause Analysis',
      category: 'logos',
      definition: 'Tracing problems to their fundamental sources rather than symptoms',
      example: 'Public health identifying poverty as root cause of multiple health disparities',
      instruction: 'Dig beyond surface symptoms. Use "5 Whys" technique. Address fundamentals.',
      usage: 'Problem-solving, policy design, systems analysis',
      origin: 'Systems thinking and problem analysis'
    },
    {
      id: 145,
      name: 'Logical Syllogism',
      category: 'logos',
      definition: 'Using formal deductive reasoning with major premise, minor premise, and conclusion',
      example: 'All humans are mortal (major). Socrates is human (minor). Therefore, Socrates is mortal (conclusion)',
      instruction: 'Ensure premises are true. Check logical validity. Make structure explicit.',
      usage: 'Philosophical argument, formal logic, structured reasoning',
      origin: 'Aristotelian logic'
    },

    // KAIROS (15 more devices - total 25)
    {
      id: 71,
      name: 'The Teachable Moment',
      category: 'kairos',
      definition: 'Seizing opportunities when audience is most receptive to learning',
      example: 'Using natural disasters to educate about climate adaptation when attention is focused',
      instruction: 'Read audience readiness. Connect to immediate experience. Avoid exploitation.',
      usage: 'Education, public health messaging, awareness campaigns',
      origin: 'Educational psychology'
    },
    {
      id: 72,
      name: 'Crisis Rhetoric',
      category: 'kairos',
      definition: 'Using emergency situations to justify rapid or extraordinary action',
      example: 'Churchill\'s wartime speeches capitalising on existential threat timing',
      instruction: 'Assess genuine crisis. Avoid false urgency. Provide clear direction.',
      usage: 'Emergency response, wartime leadership, urgent reforms',
      origin: 'Crisis communication traditions'
    },
    {
      id: 73,
      name: 'The Anniversary Invocation',
      category: 'kairos',
      definition: 'Leveraging significant dates for rhetorical power',
      example: 'Independence day speeches gain power from commemorative timing',
      instruction: 'Connect past to present. Honour significance. Look forward.',
      usage: 'Commemorative speeches, national holidays, historical milestones',
      origin: 'Commemorative rhetoric'
    },
    {
      id: 74,
      name: 'The Deadline Effect',
      category: 'kairos',
      definition: 'Creating urgency through real or constructed time limits',
      example: 'Paris Climate Agreement creating binding timeline for emissions reductions',
      instruction: 'Set realistic deadlines. Explain consequences. Provide achievable paths.',
      usage: 'Project management, policy implementation, urgent campaigns',
      origin: 'Modern project and time management'
    },
    {
      id: 75,
      name: 'Seasonal Resonance',
      category: 'kairos',
      definition: 'Timing messages to align with natural or cultural seasons',
      example: 'Spring environmental campaigns aligning with renewal and growth symbolism',
      instruction: 'Respect cultural calendar. Use natural rhythms. Connect to lived experience.',
      usage: 'Marketing, agricultural policy, cultural campaigns',
      origin: 'Agricultural and seasonal communication'
    },
    {
      id: 76,
      name: 'The Retirement Speech',
      category: 'kairos',
      definition: 'Using transitions and endings as moments of reflection and truth-telling',
      example: 'Eisenhower\'s farewell warning about military-industrial complex',
      instruction: 'Speak truths that position allowed you to see. Look to future. Release gracefully.',
      usage: 'Farewell addresses, retirement speeches, final statements',
      origin: 'Farewell address traditions'
    },
    {
      id: 77,
      name: 'The Victory Declaration',
      category: 'kairos',
      definition: 'Timing announcements at peak of success for maximum impact',
      example: 'Mandela\'s inauguration speech timed at moment of democratic victory',
      instruction: 'Celebrate genuinely. Include all contributors. Transition to next challenge.',
      usage: 'Victory speeches, achievement announcements, milestone celebrations',
      origin: 'Victory and triumph rhetoric'
    },
    {
      id: 78,
      name: 'Prophetic Timing',
      category: 'kairos',
      definition: 'Speaking uncomfortable truths before society is ready, planting seeds',
      example: 'Rachel Carson\'s Silent Spring spoke environmental truth decade before movement',
      instruction: 'Accept being ahead of time. Plant seeds patiently. Document for future.',
      usage: 'Prophetic speech, early warnings, visionary communication',
      origin: 'Prophetic and visionary traditions'
    },
    {
      id: 79,
      name: 'The Eulogy Moment',
      category: 'kairos',
      definition: 'Using death and mourning as opportunity for collective reflection',
      example: 'Obama\'s Charleston church eulogy after massacre became racial justice sermon',
      instruction: 'Honour the deceased. Connect loss to meaning. Guide collective grief.',
      usage: 'Eulogies, memorial services, collective mourning',
      origin: 'Funeral and memorial traditions'
    },
    {
      id: 80,
      name: 'The Dawn Speech',
      category: 'kairos',
      definition: 'Speaking at beginnings—new years, inaugurations, dawns—for fresh start energy',
      example: 'New Year addresses capitalising on renewal psychology',
      instruction: 'Tap into fresh start feeling. Set direction. Build momentum.',
      usage: 'Inaugurations, new year speeches, opening ceremonies',
      origin: 'Inaugural and beginning rituals'
    },
    {
      id: 81,
      name: 'The Scandal Response',
      category: 'kairos',
      definition: 'Timing of responses to controversies for optimal damage control or transformation',
      example: 'Truth and Reconciliation approach responding to apartheid at moment of transition',
      instruction: 'Respond promptly but thoughtfully. Take responsibility. Show concrete change.',
      usage: 'Crisis PR, scandal management, accountability moments',
      origin: 'Public relations and crisis communication'
    },
    {
      id: 82,
      name: 'The Grassroots Crescendo',
      category: 'kairos',
      definition: 'Timing leadership intervention at peak of popular momentum',
      example: 'Gandhi\'s leadership emerging when independence movement reached critical mass',
      instruction: 'Feel the momentum. Step forward when energy peaks. Channel, don\'t control.',
      usage: 'Social movements, popular uprisings, grassroots campaigns',
      origin: 'Social movement dynamics'
    },
    {
      id: 83,
      name: 'The Diplomatic Window',
      category: 'kairos',
      definition: 'Recognising and acting within brief opportunities for negotiation',
      example: 'Camp David Accords capitalising on rare alignment of leaders and conditions',
      instruction: 'Move quickly when window opens. Recognise closing moments. Build trust rapidly.',
      usage: 'Diplomatic negotiations, peace processes, international relations',
      origin: 'Diplomatic practice and negotiation'
    },
    {
      id: 84,
      name: 'The Cultural Shift Recognition',
      category: 'kairos',
      definition: 'Identifying and naming emerging cultural changes as they happen',
      example: 'Recognising and naming "Me Too" moment as cultural watershed in real-time',
      instruction: 'Name emerging patterns. Connect individual to collective. Crystallize moment.',
      usage: 'Cultural commentary, trend analysis, social movements',
      origin: 'Cultural criticism and sociology'
    },
    {
      id: 85,
      name: 'The Generational Handoff',
      category: 'kairos',
      definition: 'Timing leadership transitions to match generational changes',
      example: 'Youth climate leaders emerging as older environmental movement welcomes transition',
      instruction: 'Honour what came before. Welcome new energy. Enable rather than control.',
      usage: 'Leadership transitions, generational change, succession planning',
      origin: 'Generational and succession dynamics'
    },
    {
      id: 146,
      name: 'The Fresh Start',
      category: 'kairos',
      definition: 'Capitalising on new beginnings when people are open to change',
      example: 'New Year resolutions campaigns targeting people\'s readiness for personal transformation',
      instruction: 'Leverage symbolic beginnings. Provide clear pathways. Support follow-through.',
      usage: 'Behaviour change campaigns, reform initiatives, renewal movements',
      origin: 'Temporal psychology and ritual'
    },
    {
      id: 147,
      name: 'The Commemorative Moment',
      category: 'kairos',
      definition: 'Using anniversaries and memorials to reframe or renew commitments',
      example: 'Martin Luther King Jr. Day speeches connecting past struggles to present issues',
      instruction: 'Connect past to present meaningfully. Honour memory through action. Avoid empty ritual.',
      usage: 'Memorial speeches, anniversary events, historical reflection',
      origin: 'Commemorative rhetoric'
    },
    {
      id: 148,
      name: 'The Viral Moment',
      category: 'kairos',
      definition: 'Recognising and amplifying when content reaches critical mass for spread',
      example: 'Ice Bucket Challenge capitalising on viral momentum for ALS awareness',
      instruction: 'Ride the wave. Keep message clear. Enable easy participation.',
      usage: 'Digital campaigns, social media activism, awareness movements',
      origin: 'Digital and viral communication'
    },
    {
      id: 149,
      name: 'The Quiet Before',
      category: 'kairos',
      definition: 'Speaking into moments of anticipation or calm before significant change',
      example: 'Pre-election addresses setting frameworks before voters make decisions',
      instruction: 'Recognise receptive anticipation. Plant seeds. Trust the process.',
      usage: 'Pre-decision messaging, anticipatory guidance, preparatory communication',
      origin: 'Strategic communication timing'
    },
    {
      id: 150,
      name: 'The Return to Basics',
      category: 'kairos',
      definition: 'Timing calls for simplification when complexity becomes overwhelming',
      example: 'Minimalism movement emerging when consumer culture reaches saturation',
      instruction: 'Recognise saturation points. Offer relief. Make simplicity appealing.',
      usage: 'Simplification movements, back-to-basics campaigns, clarity initiatives',
      origin: 'Counter-cultural and simplification movements'
    },

    // FIGURES OF SPEECH (45 more devices - total 50)
    {
      id: 86,
      name: 'Simile',
      category: 'figures',
      definition: 'Comparing two unlike things using "like" or "as"',
      example: 'Chinua Achebe: "Age was respected among his people, but achievement was revered. As the elders said, if a child washed his hands he could eat with kings"',
      instruction: 'Make comparisons fresh and unexpected. Avoid clichés. Illuminate hidden similarities.',
      usage: 'Poetry, descriptive writing, explanatory teaching',
      origin: 'Greek: comparison, likeness'
    },
    {
      id: 87,
      name: 'Personification',
      category: 'figures',
      definition: 'Giving human qualities to non-human things',
      example: 'Pablo Neruda: "I want to do with you what spring does with the cherry trees"',
      instruction: 'Choose qualities that illuminate. Make abstract concrete. Connect emotionally.',
      usage: 'Poetry, environmental writing, creative description',
      origin: 'Latin: persona (person) + facere (to make)'
    },
    {
      id: 88,
      name: 'Parallelism',
      category: 'figures',
      definition: 'Using similar grammatical structures in successive phrases',
      example: 'Desmond Tutu: "We struggled against apartheid, we struggled for freedom, we struggled to bring dignity to all"',
      instruction: 'Match structure precisely. Build rhythm. Create memorable patterns.',
      usage: 'Speeches, persuasive writing, memorable statements',
      origin: 'Rhetorical structure and balance'
    },
    {
      id: 89,
      name: 'Rhetorical Question',
      category: 'figures',
      definition: 'Asking questions for effect, not expecting answer',
      example: 'Sojourner Truth: "Ain\'t I a woman?"',
      instruction: 'Make answer obvious. Use sparingly. Create pause for reflection.',
      usage: 'Speeches, persuasive writing, dramatic emphasis',
      origin: 'Classical oratory'
    },
    {
      id: 90,
      name: 'Antithesis',
      category: 'figures',
      definition: 'Juxtaposing contrasting ideas in balanced phrases',
      example: 'Kwame Nkrumah: "We face neither East nor West; we face forward"',
      instruction: 'Create clear opposites. Balance grammatically. Sharpen contrast.',
      usage: 'Political speeches, philosophical writing, memorable quotes',
      origin: 'Greek: antithesis (opposition)'
    },
    {
      id: 91,
      name: 'Metonymy',
      category: 'figures',
      definition: 'Substituting a closely associated word for what is meant',
      example: '"The Crown decided" meaning the British monarchy and government',
      instruction: 'Choose associations audience recognises. Ensure clarity. Use cultural knowledge.',
      usage: 'Political discourse, literary writing, symbolic communication',
      origin: 'Greek: metonymia (change of name)'
    },
    {
      id: 92,
      name: 'Synecdoche',
      category: 'figures',
      definition: 'Using a part to represent the whole, or vice versa',
      example: '"All hands on deck" using hands to mean sailors/workers',
      instruction: 'Choose representative parts. Maintain clear connection. Build imagery.',
      usage: 'Literary writing, everyday speech, symbolic representation',
      origin: 'Greek: synekdoche (simultaneous understanding)'
    },
    {
      id: 93,
      name: 'Oxymoron',
      category: 'figures',
      definition: 'Combining contradictory terms for effect',
      example: 'Wole Soyinka describing "bitter sweetness" of independence',
      instruction: 'Reveal genuine paradox. Create surprise. Illuminate complexity.',
      usage: 'Poetry, literary writing, complex descriptions',
      origin: 'Greek: oxys (sharp) + moros (dull, foolish)'
    },
    {
      id: 94,
      name: 'Irony',
      category: 'figures',
      definition: 'Saying the opposite of what is meant, often for humour or emphasis',
      example: 'Oscar Wilde: "Some cause happiness wherever they go; others whenever they go"',
      instruction: 'Ensure audience recognises irony. Use tone carefully. Avoid confusion.',
      usage: 'Satire, humour, critical commentary',
      origin: 'Greek: eironeia (feigned ignorance)'
    },
    {
      id: 95,
      name: 'Understatement (Litotes)',
      category: 'figures',
      definition: 'Deliberately representing something as less than it is',
      example: 'Winston Churchill: "This is not the end. It is not even the beginning of the end. But it is, perhaps, the end of the beginning"',
      instruction: 'Use for ironic effect. Create emphasis through minimisation. Time carefully.',
      usage: 'British communication style, ironic commentary, diplomatic speech',
      origin: 'Greek: litotes (plainness, simplicity)'
    },
    {
      id: 96,
      name: 'Asyndeton',
      category: 'figures',
      definition: 'Omitting conjunctions between words, phrases or clauses',
      example: 'Julius Caesar: "Veni, vidi, vici" (I came, I saw, I conquered)',
      instruction: 'Create rapid rhythm. Build intensity. Use for dramatic effect.',
      usage: 'Dramatic speeches, powerful statements, military rhetoric',
      origin: 'Greek: asyndeton (unconnected)'
    },
    {
      id: 97,
      name: 'Polysyndeton',
      category: 'figures',
      definition: 'Using multiple conjunctions in close succession',
      example: 'Biblical: "And God said... and it was so... and God saw that it was good"',
      instruction: 'Slow pace deliberately. Build weight. Create solemnity.',
      usage: 'Religious texts, solemn speeches, deliberate emphasis',
      origin: 'Greek: polysyndeton (bound together)'
    },
    {
      id: 98,
      name: 'Epistrophe',
      category: 'figures',
      definition: 'Repetition of words at the end of successive clauses',
      example: 'Abraham Lincoln: "government of the people, by the people, for the people"',
      instruction: 'Build to climax. Create rhythm. Make memorable.',
      usage: 'Political speeches, persuasive conclusions, memorable statements',
      origin: 'Greek: epistrophe (turning back upon)'
    },
    {
      id: 99,
      name: 'Anadiplosis',
      category: 'figures',
      definition: 'Repeating the last word of one clause at the beginning of the next',
      example: 'Yoda: "Fear leads to anger. Anger leads to hate. Hate leads to suffering"',
      instruction: 'Create chain logic. Build momentum. Connect ideas organically.',
      usage: 'Logical arguments, teaching, memorable sequences',
      origin: 'Greek: anadiplosis (doubling back)'
    },
    {
      id: 100,
      name: 'Zeugma',
      category: 'figures',
      definition: 'Using one word to govern multiple parts of sentence in different senses',
      example: 'Alexander Pope: "Or stain her honour or her new brocade"',
      instruction: 'Create surprise. Use for humour or emphasis. Maintain clarity.',
      usage: 'Literary writing, witty commentary, stylistic flair',
      origin: 'Greek: zeugma (yoking, bonding)'
    },
    {
      id: 101,
      name: 'Apostrophe',
      category: 'figures',
      definition: 'Addressing someone or something absent or non-human',
      example: 'Pablo Neruda addressing the sea: "O sea, you are my sorrow and my joy"',
      instruction: 'Make address feel natural. Create emotional connection. Use sparingly.',
      usage: 'Poetry, dramatic speeches, emotional expression',
      origin: 'Greek: apostrophe (turning away)'
    },
    {
      id: 102,
      name: 'Allusion',
      category: 'figures',
      definition: 'Indirect reference to literary, historical, or cultural figures/events',
      example: 'Referring to someone as a "Good Samaritan" alludes to Biblical parable',
      instruction: 'Know your audience. Ensure references are accessible. Add layers of meaning.',
      usage: 'Literary writing, educated discourse, cultural commentary',
      origin: 'Latin: alludere (to play with, jest)'
    },
    {
      id: 103,
      name: 'Euphemism',
      category: 'figures',
      definition: 'Substituting mild or vague expression for harsh or direct one',
      example: '"Passed away" instead of "died," "let go" instead of "fired"',
      instruction: 'Use judiciously. Don\'t obscure truth. Respect cultural norms.',
      usage: 'Polite speech, sensitive topics, diplomatic communication',
      origin: 'Greek: euphemismos (good speech)'
    },
    {
      id: 104,
      name: 'Onomatopoeia',
      category: 'figures',
      definition: 'Words that imitate sounds they describe',
      example: 'Gabriel García Márquez: "The distant drumming grew louder—boom, boom, boom"',
      instruction: 'Choose sound-appropriate words. Create sensory experience. Use in description.',
      usage: 'Poetry, descriptive writing, children\'s literature',
      origin: 'Greek: onomatopoiia (making of names)'
    },
    {
      id: 105,
      name: 'Climax (Gradatio)',
      category: 'figures',
      definition: 'Arranging words or ideas in order of increasing importance',
      example: 'Shakespeare: "I came, I saw, I conquered"—building to final triumph',
      instruction: 'Arrange carefully by weight. Build to strong conclusion. Create momentum.',
      usage: 'Persuasive speeches, dramatic writing, powerful conclusions',
      origin: 'Greek: klimax (ladder)'
    },
    {
      id: 106,
      name: 'Anticlimax',
      category: 'figures',
      definition: 'Abrupt shift from important to trivial, often for humorous effect',
      example: 'Oscar Wilde: "I have nothing to declare except my genius"',
      instruction: 'Use for humour or irony. Create surprise. Subvert expectations.',
      usage: 'Satire, humour, ironic commentary',
      origin: 'Opposite of climax'
    },
    {
      id: 107,
      name: 'Polyptoton',
      category: 'figures',
      definition: 'Repeating words derived from same root in different forms',
      example: 'Shakespeare: "With eager feeding food doth choke the feeder"',
      instruction: 'Show word relationships. Create rhythm. Build connections.',
      usage: 'Literary writing, wordplay, emphatic expression',
      origin: 'Greek: polyptoton (many cases)'
    },
    {
      id: 108,
      name: 'Anthimeria',
      category: 'figures',
      definition: 'Using one part of speech as another (noun as verb, etc.)',
      example: 'Shakespeare: "But me no buts" or modern "I\'ll Google it"',
      instruction: 'Create freshness. Use sparingly. Ensure clarity.',
      usage: 'Creative writing, modern slang, stylistic innovation',
      origin: 'Greek: anthimeria (substitution)'
    },
    {
      id: 109,
      name: 'Hendiadys',
      category: 'figures',
      definition: 'Expressing one idea with two words connected by "and"',
      example: 'Shakespeare: "sound and fury" instead of "furious sound"',
      instruction: 'Give equal weight to components. Create emphasis. Build complexity.',
      usage: 'Literary writing, poetic expression, emphatic speech',
      origin: 'Greek: hen dia dyoin (one through two)'
    },
    {
      id: 110,
      name: 'Synesthesia',
      category: 'figures',
      definition: 'Describing one sense through another',
      example: 'Rumi: "The taste of prayer" or Baudelaire: "perfumes green as prairies"',
      instruction: 'Cross sensory boundaries. Create vivid imagery. Evoke experience.',
      usage: 'Poetry, descriptive writing, experiential communication',
      origin: 'Greek: synaisthesis (simultaneous perception)'
    },
    {
      id: 111,
      name: 'Tmesis',
      category: 'figures',
      definition: 'Splitting a word by inserting another word inside',
      example: '"Abso-blooming-lutely" or "Un-freaking-believable"',
      instruction: 'Use for emphasis or humour. Respect language. Use sparingly.',
      usage: 'Informal speech, emphasis, humorous effect',
      origin: 'Greek: tmesis (cutting)'
    },
    {
      id: 112,
      name: 'Enallage',
      category: 'figures',
      definition: 'Deliberate grammatical mistake for stylistic effect',
      example: '"We was robbed!" for emphasis over "We were robbed"',
      instruction: 'Use intentionally. Create character voice. Respect context.',
      usage: 'Character dialogue, regional speech, stylistic choice',
      origin: 'Greek: enallage (interchange)'
    },
    {
      id: 113,
      name: 'Metalepsis',
      category: 'figures',
      definition: 'Figurative language building on figurative language',
      example: 'Saying "close the door" when meaning "leave" (door is metonymy for leaving)',
      instruction: 'Layer meanings carefully. Ensure accessibility. Create depth.',
      usage: 'Literary writing, complex metaphors, layered communication',
      origin: 'Greek: metalepsis (participation, substitution)'
    },
    {
      id: 114,
      name: 'Symploce',
      category: 'figures',
      definition: 'Combining anaphora and epistrophe—repetition at both beginning and end',
      example: 'Obama: "We cannot walk alone. And as we walk, we cannot turn back"',
      instruction: 'Create powerful rhythm. Build intensity. Use for climactic moments.',
      usage: 'Political speeches, persuasive climaxes, memorable statements',
      origin: 'Greek: symploke (interweaving)'
    },
    {
      id: 115,
      name: 'Isocolon',
      category: 'figures',
      definition: 'Parallel structures of equal length',
      example: 'Julius Caesar: "Veni, vidi, vici" (three two-syllable words)',
      instruction: 'Match length precisely. Create rhythm. Make memorable.',
      usage: 'Memorable phrases, balanced statements, rhythmic speech',
      origin: 'Greek: isocolon (equal members)'
    },
    {
      id: 116,
      name: 'Tricolon',
      category: 'figures',
      definition: 'Series of three parallel elements',
      example: 'Abraham Lincoln: "government of the people, by the people, for the people"',
      instruction: 'Use rule of three. Build to third element. Create completeness.',
      usage: 'Memorable phrases, persuasive points, structural clarity',
      origin: 'Greek: three (tri) + members (colon)'
    },
    {
      id: 117,
      name: 'Aphorism',
      category: 'figures',
      definition: 'Concise statement of principle or truth',
      example: 'Chinua Achebe: "Until the lions have their own historians, the history of the hunt will always glorify the hunter"',
      instruction: 'Distill to essence. Make memorable. Express universal through specific.',
      usage: 'Wisdom literature, memorable quotes, teaching',
      origin: 'Greek: aphorismos (definition, distinction)'
    },
    {
      id: 118,
      name: 'Maxim',
      category: 'figures',
      definition: 'General truth or rule of conduct expressed succinctly',
      example: 'African proverb: "If you want to go fast, go alone. If you want to go far, go together"',
      instruction: 'Express wisdom simply. Make applicable. Connect to experience.',
      usage: 'Teaching, cultural wisdom, practical guidance',
      origin: 'Latin: maxima (greatest/most important proposition)'
    },
    {
      id: 119,
      name: 'Epigram',
      category: 'figures',
      definition: 'Brief, witty statement, often paradoxical',
      example: 'Oscar Wilde: "I can resist everything except temptation"',
      instruction: 'Create surprise. Use wit. Express paradox elegantly.',
      usage: 'Witty writing, social commentary, memorable quotes',
      origin: 'Greek: epigramma (inscription)'
    },
    {
      id: 120,
      name: 'Proverb',
      category: 'figures',
      definition: 'Traditional saying expressing common truth or useful advice',
      example: 'Yoruba proverb: "However far the stream flows, it never forgets its source"',
      instruction: 'Draw from cultural wisdom. Apply to context. Honour origins.',
      usage: 'Teaching, cultural transmission, practical wisdom',
      origin: 'Universal cultural traditions'
    },
    {
      id: 121,
      name: 'Parable',
      category: 'figures',
      definition: 'Simple story illustrating moral or spiritual lesson',
      example: 'Jesus\'s Good Samaritan parable teaching compassion across boundaries',
      instruction: 'Keep story simple. Make lesson clear but not heavy-handed. Allow reflection.',
      usage: 'Teaching, religious instruction, moral education',
      origin: 'Greek: parabole (comparison)'
    },
    {
      id: 122,
      name: 'Allegory',
      category: 'figures',
      definition: 'Extended metaphor where story elements represent something else',
      example: 'George Orwell\'s Animal Farm as allegory for Russian Revolution',
      instruction: 'Maintain consistency. Work on surface level. Allow deeper reading.',
      usage: 'Literary writing, political commentary, teaching',
      origin: 'Greek: allegoria (speaking otherwise)'
    },
    {
      id: 123,
      name: 'Parataxis',
      category: 'figures',
      definition: 'Placing clauses or phrases side by side without conjunctions',
      example: 'Ernest Hemingway: "I came. I saw. I left."',
      instruction: 'Create staccato rhythm. Build intensity. Use for directness.',
      usage: 'Modern prose, dramatic effect, minimalist style',
      origin: 'Greek: parataxis (arrangement side by side)'
    },
    {
      id: 124,
      name: 'Hypotaxis',
      category: 'figures',
      definition: 'Subordinating clauses to show relationships and complexity',
      example: 'Proust\'s long sentences showing how one thought leads to another',
      instruction: 'Show relationships clearly. Don\'t lose reader. Build complexity.',
      usage: 'Academic writing, complex narration, analytical prose',
      origin: 'Greek: hypotaxis (arrangement under)'
    },
    {
      id: 125,
      name: 'Ellipsis',
      category: 'figures',
      definition: 'Omitting words while maintaining meaning',
      example: '"The man with the white hat" instead of "The man who is wearing the white hat"',
      instruction: 'Ensure clarity. Create economy. Maintain grammatical sense.',
      usage: 'Efficient prose, implied meaning, stylistic compression',
      origin: 'Greek: elleipsis (falling short, deficiency)'
    },
    {
      id: 126,
      name: 'Prolepsis',
      category: 'figures',
      definition: 'Anticipating and answering objections before they\'re raised',
      example: '"You might say this is expensive, but consider the long-term savings"',
      instruction: 'Address real objections. Strengthen argument. Show you\'ve considered alternatives.',
      usage: 'Persuasive writing, debate, anticipatory argumentation',
      origin: 'Greek: prolepsis (anticipation)'
    },
    {
      id: 127,
      name: 'Apophasis',
      category: 'figures',
      definition: 'Mentioning something by saying you won\'t mention it',
      example: '"I won\'t mention my opponent\'s poor record..." (while mentioning it)',
      instruction: 'Use strategically. Create emphasis. Acknowledge manipulation openly.',
      usage: 'Political rhetoric, subtle criticism, ironic communication',
      origin: 'Greek: apophasis (denial)'
    },
    {
      id: 128,
      name: 'Distinctio',
      category: 'figures',
      definition: 'Explicitly defining or specifying meaning of word or phrase',
      example: '"By freedom I mean not merely political rights but economic opportunity"',
      instruction: 'Clarify ambiguous terms. Control meaning. Prevent misunderstanding.',
      usage: 'Academic writing, definitional arguments, precise communication',
      origin: 'Latin: distinctio (distinction, difference)'
    },
    {
      id: 129,
      name: 'Hypophora',
      category: 'figures',
      definition: 'Asking question then immediately answering it',
      example: '"What does it take to succeed? Hard work, dedication, and perseverance"',
      instruction: 'Control conversation. Guide thinking. Maintain momentum.',
      usage: 'Teaching, persuasive writing, explanatory speech',
      origin: 'Greek: hypophora (carrying under)'
    },
    {
      id: 130,
      name: 'Aposiopesis',
      category: 'figures',
      definition: 'Breaking off mid-sentence, leaving thought incomplete',
      example: '"If you don\'t stop, I\'ll—" [trailing off threateningly]',
      instruction: 'Create suspense. Allow imagination. Use dramatic silence.',
      usage: 'Dramatic writing, implied threats, emotional speech',
      origin: 'Greek: aposiopesis (becoming silent)'
    }
  ];
